/* globals functionality, views, components, showModalConfirm, showModalError, showModalInfo*/
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import Request from "common/Request";
import { isIPv4IPv6Range, isValidIPValue, safeStr } from "common/api";
import BqnContext from "common/BqnContext";
import Table from "common/graphs/Table/Table";
import Modal from "common/Modal";

const createPlaceholder = () => `ADDRESS`;

const propagateHeaderIntoRowType = (input, bqnId, nBqnsActive) => {
  const [head, ...rows] = input.trim().split("\n");
  const [_, column] = head.split(/\s+/);
  const type =
    column === "ADDRESS"
      ? "IP-ADDRESS"
      : column === "RANGE"
        ? "IP-ADDR-RANGE"
        : column === "VLAN"
          ? "VLAN-ID"
          : column; // SUBSCRIBER-ID

  const count = {};
  for (const row of rows) {
    const [_, value] = row.trim().split(/\s+/);
    count[value] = (count[value] || 0) + 1;
  }

  const included = new Set();

  return rows
    .filter((row) => {
      const [bqn, value] = row.trim().split(/\s+/);
      if (bqnId === "all") {
        if (!included.has(value)) {
          included.add(value);
          return true;
        }

        return count[value] < nBqnsActive;
      } else {
        return parseFloat(bqnId) === parseFloat(bqn);
      }
    })
    .map((row) => {
      const [bqn, value] = row.trim().split(/\s+/);
      const bqnDisplayed =
        bqnId === "all" && count[value] === nBqnsActive ? "All" : bqn;
      return `${bqnDisplayed} ${type} ${value}`;
    });
};

const ensurePlaceholder = () => (error) => {
  return { response: createPlaceholder() };
};

// None of these four commands has a "bqn-id ID" variant, so the whole cluster
// answers and every row reports its BQN: the ones of the BQN selected are
// taken from them, and a value present in every BQN is shown as "All".
const doIpAddressesRequest = (subscriberGroup, bqnId, nBqnsActive) =>
  Promise.all([
    ifCl.run(
      `show api subscriber-group name ${safeStr(subscriberGroup)} subscriber`
    ),
    ifCl.run(
      `show api subscriber-group name ${safeStr(
        subscriberGroup
      )} subscriber-range`
    ),
    ifCl.run(
      `show api subscriber-group name ${safeStr(subscriberGroup)} subscriber-id`
    ),
    ifCl.run(`show api subscriber-group name ${safeStr(subscriberGroup)} vlan`),
  ])
    .then((responses) => {
      const response = [
        "BQN TYPE VALUE",
        ...responses.flatMap((input) =>
          propagateHeaderIntoRowType(input, bqnId, nBqnsActive)
        ),
      ].join("\n");

      return { response };
    })
    .catch(ensurePlaceholder());

const ResultsLayout = styled.div`
  margin-right: 5px;
  flex: 100%;
  overflow: hidden; /*list will scroll*/
`;

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  --is-highest: #3d85c6;
  --is-higher: #6aa84f;
  --is-lower: #e69138;
  --is-lowest: #cc0000;
  --warning: #cc0000;
  .inverted {
    --is-lowest: #3d85c6;
    --is-lower: #6aa84f;
    --is-higher: #e69138;
    --is-highest: #cc0000;
  }
  .dataTable tbody td {
    transition: color 1s, background-color: 1s;
  }
  &.colorize-warnings .dataTable tbody td {
    &.warning {
      color: white;
      background: var(--warning);
    }
  }
  table.MuiTable-root {
    min-width: auto !important;
    max-width: auto !important;
  }
`;

const getColumns = (mode, actions, subscriberGroup, reloadWhenDone) => {
  return [
    ...(mode !== "standalone"
      ? [
        {
          label: "BQN",
          id: "BQN",
          numeric: false, // alignment reasons
        },
      ]
      : []),
    {
      label: "TYPE",
      id: "TYPE",
      numeric: false,
    },
    {
      label: "VALUE",
      id: "VALUE",
      numeric: false,
      cellClassName: (value, row) =>
        ["IP-ADDRESS", "SUBSCRIBER-ID"].includes(row["TYPE"])
          ? "hyperlink-text"
          : "",
      onClick: (value, row) => {
        const type = row["TYPE"];
        if (type === "IP-ADDRESS") {
          openSubscriberDetails({ addr: value });
        } else if (type === "SUBSCRIBER-ID") {
          openSubscriberDetails({ subsId: value });
        }
      },
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      sort: false,
      actions: [
        {
          label: "Delete",
          icon: "delete",
          onClick: (row) => {
            const type = row["TYPE"];
            const value = row["VALUE"];

            showModalConfirm(
              `Delete ${value} from subscriber group?`,
              "",
              "delete_forever",
              () => {
                let clearCmd = "";
                switch (type) {
                  case "IP-ADDRESS":
                    clearCmd = `clear api subscriber ${value} subscriber-group ${safeStr(
                      subscriberGroup
                    )}`;
                    break;
                  case "SUBSCRIBER-ID":
                    clearCmd = `clear api subscriber-group ${safeStr(
                      subscriberGroup
                    )} subscriber-id ${value}`;
                    break;
                  case "VLAN-ID":
                    clearCmd = `clear api subscriber-group ${safeStr(
                      subscriberGroup
                    )} vlan ${value}`;
                    break;
                  case "IP-ADDR-RANGE":
                    clearCmd = `clear api subscriber-group ${safeStr(
                      subscriberGroup
                    )} subscriber-range ${value}`;
                    break;
                }

                ifCl
                  .run(clearCmd)
                  .then(reloadWhenDone)
                  .catch((error) => showModalError(error));
              }
            );
          },
        },
      ],
    },
  ];
};

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  subscriberGroup,
  whenDone,
}) => {
  const handleApply = () => {
    const cmds = items.map((row) => {
      const type = row["TYPE"];
      const value = row["VALUE"];
      switch (type) {
        case "IP-ADDRESS":
          return `clear api subscriber ${value} subscriber-group ${safeStr(subscriberGroup)}`;
        case "SUBSCRIBER-ID":
          return `clear api subscriber-group ${safeStr(subscriberGroup)} subscriber-id ${value}`;
        case "VLAN-ID":
          return `clear api subscriber-group ${safeStr(subscriberGroup)} vlan ${value}`;
        case "IP-ADDR-RANGE":
          return `clear api subscriber-group ${safeStr(subscriberGroup)} subscriber-range ${value}`;
        default:
          return "";
      }
    }).filter((cmd) => cmd !== "");

    ifCl
      .run(`${cmds.join("\n")}\n`, true)
      .then((response) => {
        if (response.length === 0) {
          setShowDeleteModal(false);
          whenDone();
        } else if (response.substring(0, 5) === "%WARN") {
          showModalInfo("Warning:", response);
          setShowDeleteModal(false);
          whenDone();
        } else {
          showModalError("Error:", response);
        }
      })
      .catch((error) => showModalError(error, ""));
  };

  if (!showDeleteModal) {
    return null;
  }

  const title = items.length === 1 ? `Remove value?` : `Remove ${items.length} values?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {items.length === 1 ? <p>WARNING: Value data will be lost</p> : null}
          {items.length > 1 ? (
            <>
              {items.length < 100 ? (
                <ul>{items.map((row, idx) => (<li key={idx}>{row["VALUE"]}</li>))}</ul>
              ) : null}
              <p>WARNING: Values data will be lost</p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
    />
  );
};

const ListValues = ({ request, subscriberGroup }) => {
  const actions = useContext(ActionsContext);
  const reloadWhenDone = () => actions.send("do-load-subscriber-groups-edit");
  const clusterStatus = useContext(BqnContext).clusterStatus;
  const mode = clusterStatus?.mode;
  const [selected, setSelected] = useState([]);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const openSubscriberDetails = (target) => {
    const returnTo = "viewStatusSubscriberGroupsEdit";
    globalNavigate("viewSubsDetail", { returnView: returnTo, ...target });
  };

  const columns = getColumns(mode, actions, subscriberGroup, reloadWhenDone);

  return (
    <TableContainer className={"colorize-warnings"}>
      <Request during={request}>
        {({ response }) => {
          const [header, ...rows] = response.trim().split("\n");
          const headerAttrs = header.trim().split(/\s+/);
          const newRows = rows.map((row, index) => {
            const rowAttrs = row.trim(/\s/).split(/\s+/);
            const newEntry = rowAttrs.reduce((acc, attr, i) => {
              acc[headerAttrs[i]] = attr;
              return acc;
            }, {});
            newEntry.id = index;
            return newEntry;
          });

          return (
            <>
              <Table
                isSelectable={true}
                isRowSelectable={false}
                data={newRows}
                sort={true}
                columns={columns}
                search={true}
                csvPrefix="subscriber-group-values"
                rowsPerPageNr={25}
                selected={selected}
                setSelected={setSelected}
                deleteSelected={(values) => {
                  const selectedItems = newRows.filter((row) => values.includes(row.id));
                  setDeleteSelected(selectedItems);
                  setShowDeleteModal(true);
                }}
              />
              <DeleteModal
                showDeleteModal={showDeleteModal}
                setShowDeleteModal={setShowDeleteModal}
                items={deleteSelected}
                subscriberGroup={subscriberGroup}
                whenDone={() => {
                  setSelected([]);
                  reloadWhenDone();
                }}
              />
            </>
          );
        }}
      </Request>
    </TableContainer>
  );
};

export const ValuesTable = ({ subscriberGroup }) => {
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);
  const { bqnId, bqns } = useContext(BqnContext);
  const nBqnsActive = bqns.filter((bqn) => bqn.function !== "offline").length;

  const doLoad = () =>
    setRequest(doIpAddressesRequest(subscriberGroup, bqnId, nBqnsActive));

  useEffect(() => {
    doLoad();
    return actions.recv("do-load-subscriber-groups-edit", doLoad);
  }, [bqnId]);

  return request === null ? null : (
    <ResultsLayout>
      <ListValues request={request} subscriberGroup={subscriberGroup} />
    </ResultsLayout>
  );
};
